

import { ChevronDown, ChevronUp, Users, Code, Database, LineChart, Headphones, Building } from "lucide-react";
import React, { useState } from "react";

// Define our SAP staffing solution categories
const staffingSolutions = [
  {
    id: 1,
    title: "SAP Functional Consultants",
    icon: <Users size={24} style={{ color: "#FF6B35" }} />,
    description: "Expert consultants specializing in key SAP functional modules and applications.",
    color: "#FF6B35",
    expertise: [
      { name: "FICO", description: "Financial Accounting & Controlling" },
      { name: "MM/SD", description: "Materials Management & Sales Distribution" },
      { name: "PP", description: "Production Planning" },
      { name: "HCM", description: "Human Capital Management" },
      { name: "SuccessFactors", description: "Cloud-based HR Solutions" }
    ],
    hideExpertiseCards: true,
    whyChoosePoints: [
      "Industry-certified consultants with 5+ years of hands-on SAP experience",
      "Deep domain expertise across manufacturing, retail, healthcare, and finance sectors",
      "Proven track record of successful implementations across 50+ Fortune 500 companies",
      "Agile methodology adoption with 40% faster project delivery times",
      "24/7 support coverage with multilingual capabilities",
      "Change management expertise to ensure smooth user adoption",
      "Cost-effective solutions reducing operational expenses by up to 30%",
      "Continuous learning programs keeping our team updated with latest SAP innovations"
    ]
  },
  {
    id: 2,
    title: "SAP Technical Experts",
    icon: <Code size={24} style={{ color: "#00377A" }} />,
    description: "Technical specialists delivering custom solutions and system maintenance.",
    color: "#00377A",
    expertise: [
      { name: "ABAP Developers", description: "Custom Development & Enhancements" },
      { name: "BASIS Administrators", description: "System Administration & Security" },
      { name: "SAP BTP", description: "Business Technology Platform Solutions" },
      { name: "Integration Experts", description: "Seamless System Connectivity" }
    ],
    hideExpertiseCards: true,
    whyChoosePoints: [
      "Certified SAP developers with expertise in latest ABAP 7.5+ and RESTful programming",
      "Advanced security protocols ensuring 99.9% system uptime and data protection",
      "Cloud-native architecture specialists with extensive SAP BTP experience",
      "Performance optimization experts achieving 60% faster system response times",
      "DevOps integration capabilities with automated deployment pipelines",
      "Custom integration solutions connecting SAP with 100+ third-party applications",
      "Proactive monitoring and maintenance reducing system downtime by 85%",
      "Code quality assurance with automated testing frameworks and peer reviews"
    ]
  },
  {
    id: 3,
    title: "SAP S/4HANA Specialists",
    icon: <Database size={24} style={{ color: "#4C9AFF" }} />,
    description: "Implementation experts for next-generation ERP solutions.",
    color: "#4C9AFF",
    expertise: [
      { name: "Migration", description: "Legacy to S/4HANA Transition" },
      { name: "Conversion", description: "System & Data Transformation" },
      { name: "Greenfield Implementations", description: "Fresh Start Deployments" },
      { name: "Brownfield Implementations", description: "Existing System Upgrades" }
    ]
  },
  {
    id: 4,
    title: "SAP Project & Program Managers",
    icon: <LineChart size={24} style={{ color: "#172B4D" }} />,
    description: "Leadership excellence ensuring successful project delivery.",
    color: "#172B4D",
    expertise: [
      { name: "End-to-end Delivery", description: "Full Project Lifecycle Management" },
      { name: "Global SAP Rollouts", description: "Multi-region Implementation Strategy" },
      { name: "Enterprise Implementations", description: "Large-scale Solution Deployment" },
      { name: "Program Governance", description: "Multi-project Oversight & Alignment" }
    ],
    hideExpertiseCards: true,
    whyChoosePoints: [
      "PMP and SAP-certified project managers with 10+ years of enterprise implementation experience",
      "Risk mitigation strategies reducing project delays by 70% and budget overruns by 50%",
      "Global delivery model with teams across 15+ countries and time zones",
      "Stakeholder management excellence with 95% client satisfaction ratings",
      "Agile and hybrid methodologies tailored to your organizational needs",
      "Executive reporting and governance frameworks for complete transparency",
      "Change management and training programs ensuring 90%+ user adoption rates",
      "Post-implementation support and optimization for sustained business value"
    ]
  },
  {
    id: 5,
    title: "Support & Enhancement Teams",
    icon: <Headphones size={24} style={{ color: "#5E6C84" }} />,
    description: "Ensuring stable operations and continuous improvement of SAP systems.",
    color: "#5E6C84",
    expertise: [
      { name: "L1–L3 Support", description: "Tiered Technical Assistance" },
      { name: "AMS Staffing", description: "Application Management Services" },
      { name: "System Optimization", description: "Performance Enhancement" },
      { name: "Ongoing Maintenance", description: "Proactive System Care" }
    ]
  }
];

// Colors palette
const colors = {
  primary: "#FF6B35",
  secondary: "black",
  accent: "#00377A",
  light: "#E6F0FF",
  highlight: "#4C9AFF", 
  text: "#172B4D",
  lightText: "#5E6C84"
};

// Expertise Badge component
const ExpertiseBadge = ({ name, color }) => (
  <div className="relative">
    <div
      className="h-10 px-4 rounded-full flex items-center justify-center text-white font-medium text-sm relative z-10 shadow transition-transform duration-300 hover:scale-105"
      style={{ backgroundColor: color }}
    >
      {name}
    </div>
    <div
      className="absolute top-0 left-0 w-full h-full rounded-full animate-pulse opacity-20"
      style={{ backgroundColor: color }}
    />
  </div>
);

// Expertise Card component
const ExpertiseCard = ({ expertise, color }) => (
  <div className="flex flex-col p-4 rounded-lg bg-white shadow mb-3 hover:shadow-md transition-all duration-300 border-l-4"
    style={{ borderColor: color }}>
    <div className="flex items-center">
      <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: color }} />
      <h4 className="font-semibold">{expertise.name}</h4>
    </div>
    <p className="text-sm text-gray-600 mt-2">{expertise.description}</p>
  </div>
);

// Enhanced Why Choose component
const WhyChooseSection = ({ solution }) => (
  <div className="mt-8 p-6 rounded-lg shadow-lg bg-gradient-to-br from-white to-gray-50">
    <div className="flex items-center mb-4">
      <div className="w-4 h-4 rounded-full mr-3 animate-pulse" style={{ backgroundColor: solution.color }}></div>
      <h5 className="text-lg font-bold" style={{ color: solution.color }}>Why Choose Our {solution.title}</h5>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {solution.whyChoosePoints ? solution.whyChoosePoints.map((point, index) => (
        <div key={index} className="flex items-start p-3 rounded-lg bg-white shadow-sm hover:shadow-md transition-all duration-300">
          <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: solution.color }}></div>
          <p className="text-sm text-gray-700 leading-relaxed">{point}</p>
        </div>
      )) : (
        <div className="col-span-2">
          <p className="text-sm text-gray-600">
            Our team delivers expert {solution.expertise.map(e => e.name).join(', ')} support, ensuring your SAP ecosystem operates at peak performance.
          </p>
        </div>
      )}
    </div>
  </div>
);

// Enhanced Infographic Component
const InfographicSVG = () => (
  <div className="w-full max-w-4xl mx-auto p-4">
    <svg viewBox="0 0 800 500" className="w-full h-auto">
      {/* Background elements */}
      <defs>
        <linearGradient id="bg-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={`${colors.light}`} />
          <stop offset="100%" stopColor="#ffffff" />
        </linearGradient>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
          <feOffset dx="2" dy="2" result="offsetblur" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.2" />
          </feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      
      {/* Background */}
      <rect x="0" y="0" width="800" height="500" fill="url(#bg-gradient)" rx="20" ry="20" />
      
      {/* Center circle */}
      <circle cx="400" cy="250" r="80" fill="white" stroke={colors.primary} strokeWidth="2" filter="url(#shadow)" />
      <text x="400" y="240" textAnchor="middle" fontWeight="bold" fontSize="18" fill={colors.text}>SAP</text>
      <text x="400" y="265" textAnchor="middle" fontWeight="bold" fontSize="18" fill={colors.text}>EXPERTISE</text>
      <line x1="370" y1="280" x2="430" y2="280" stroke={colors.primary} strokeWidth="3" />
      
      {/* Connecting lines and solution nodes */}
      {[
        { angle: -60, name: "Functional", color: colors.primary, icon: "Users" },
        { angle: -120, name: "Technical", color: colors.accent, icon: "Code" },
        { angle: 0, name: "S/4HANA", color: colors.highlight, icon: "Database" },
        { angle: 60, name: "Project Mgmt", color: colors.text, icon: "LineChart" },
        { angle: 120, name: "Support", color: colors.lightText, icon: "Headphones" }
      ].map((item, index) => {
        const rad = item.angle * Math.PI / 180;
        const x1 = 400 + 80 * Math.cos(rad);
        const y1 = 250 + 80 * Math.sin(rad);
        const x2 = 400 + 180 * Math.cos(rad);
        const y2 = 250 + 180 * Math.sin(rad);
        
        return (
          <g key={index}>
            {/* Connecting line */}
            <line 
              x1={x1} y1={y1} 
              x2={x2} y2={y2} 
              stroke={item.color} 
              strokeWidth="2" 
              strokeDasharray="5,5" 
            />
            
            {/* Solution node */}
            <circle 
              cx={x2} cy={y2} 
              r="45" 
              fill="white" 
              stroke={item.color} 
              strokeWidth="2" 
              filter="url(#shadow)" 
            />
            
            {/* Solution text */}
            <text 
              x={x2} y={y2-10} 
              textAnchor="middle" 
              fontWeight="bold" 
              fontSize="14" 
              fill={item.color}
            >
              SAP
            </text>
            <text 
              x={x2} y={y2+12} 
              textAnchor="middle" 
              fontWeight="bold" 
              fontSize="12" 
              fill={colors.text}
            >
              {item.name}
            </text>
            
            {/* Small indicator */}
            <circle 
              cx={x2} cy={y2+30} 
              r="4" 
              fill={item.color} 
            />
          </g>
        );
      })}
    </svg>
  </div>
);

// Default Content with image or infographic fallback
const DefaultContent = () => {
  // Image URL - set this to your actual image URL or leave empty to show infographic
  const [imageUrl, setImageUrl] = useState("https://img.freepik.com/free-photo/people-office-analyzing-checking-finance-graphs_23-2150377138.jpg?uid=R117373197&ga=GA1.1.1762279399.1733727878&semt=ais_hybrid&w=740")
  // Check if image is successfully loaded
  const [imageLoaded, setImageLoaded] = useState(false);
  // Image error handling
  const [imageError, setImageError] = useState(false);
  
  // Handle image load success
  const handleImageLoad = () => {
    setImageLoaded(true);
  };
  
  // Handle image load error
  const handleImageError = () => {
    setImageError(true);
  };
  
  // Determine if we should show the infographic (no image or image failed to load)
  const showInfograhic = !imageUrl || imageError;
  
  return (
    <div className="h-full flex flex-col items-center justify-center">
      {/* Header */}
      <div className="text-center mb-2">
        <h2 className="text-2xl md:text-2xl font-semibold mb-3" style={{ color: colors.primary }}>
          Complete SAP Staffing Solutions
        </h2>
        <p className="text-base md:text-lg max-w-xl mx-auto" style={{ color: colors.accent }}>
          Explore our specialized SAP expertise and staffing solutions tailored to your business needs.
        </p>
      </div>
    
      {/* Conditional rendering based on image availability */}
      {!showInfograhic ? (
        <div className="max-w-4xl mx-auto mb-2 text-center">
          <img 
            src={imageUrl} 
            alt="SAP Staffing Solutions Infographic"
            className="max-w-full h-auto rounded-lg shadow-lg mx-auto"
            style={{ display: imageLoaded ? 'block' : 'none' }}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
          {!imageLoaded && !imageError && (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-t-4 rounded-full animate-spin" 
                style={{ borderColor: `${colors.light}`, borderTopColor: colors.primary }}></div>
            </div>
          )}
        </div>
      ) : (
        <div className="w-full">
          {/* Custom SVG Infographic as fallback */}
          <InfographicSVG />
        </div>
      )}
    </div>
  );
};

const SAPStaffingSolutions = () => {
  const [expandedId, setExpandedId] = useState(null);
  const toggleAccordion = id => setExpandedId(prev => (prev === id ? null : id));

  return (
    <div className="w-full flex flex-col lg:flex-row bg-gray-50 rounded-xl overflow-hidden shadow border border-gray-100">
      {/* Accordion Column */}
      <div className="w-full lg:w-1/3 p-4 md:p-6 bg-white shadow-inner">
        <h2 className="text-xl md:text-2xl font-bold mb-2" style={{ color: colors.primary }}>SAP Staffing Solutions</h2>
        <p className="text-xs md:text-sm text-gray-600 mb-4">Specialized expertise for your SAP implementation and support needs</p>
        <div className="space-y-2">
          {staffingSolutions.map(sol => (
            <button key={sol.id} onClick={() => toggleAccordion(sol.id)}
              className="w-full flex items-center justify-between p-3 md:p-4 rounded transition-all duration-300"
              style={{
                backgroundColor: expandedId === sol.id ? `${sol.color}15` : '#ffffff',
                borderLeft: expandedId === sol.id ? `4px solid ${sol.color}` : '4px solid transparent'
              }}>
              <div className="flex items-center">
                <div className="p-2 rounded" style={{ backgroundColor: `${sol.color}20` }}>
                  {sol.icon}
                </div>
                <span className="ml-3 text-sm md:text-base font-medium"
                  style={{ color: expandedId === sol.id ? sol.color : '#4b5563' }}>
                  {sol.title}
                </span>
              </div>
              {expandedId === sol.id ?
                <ChevronUp size={18} style={{ color: sol.color }} /> :
                <ChevronDown size={18} style={{ color: '#9ca3af' }} />
              }
            </button>
          ))}
        </div>
        <div className="mt-6 p-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded border border-gray-200">
          <p className="text-xs text-gray-500 italic">
            "Our SAP staffing solutions provide specialized expertise exactly when and where you need it."          
          </p>
        </div>
      </div>

      {/* Content Column */}
      <div className="w-full lg:w-2/3 p-4 md:p-6 min-h-[24rem]" style={{ backgroundColor: colors.light }}>
        {expandedId === null ? <DefaultContent /> : staffingSolutions.map(sol => (
          expandedId === sol.id && (
            <div key={sol.id} className="animate-fadeIn">
              <div className="flex flex-col md:flex-row items-start md:items-center mb-6">
                <div className="p-3 mb-3 md:mb-0 md:mr-4 rounded-full shadow-inner"
                  style={{ backgroundColor: `${sol.color}20` }}>
                  {React.cloneElement(sol.icon, { size: 32 })}
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold" style={{ color: sol.color }}>{sol.title}</h3>
                  <p className="text-gray-700 mt-1">{sol.description}</p>
                </div>
              </div>
              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-6">
                {sol.expertise.map((item,i) => <ExpertiseBadge key={i} name={item.name} color={sol.color} />)}
              </div>
              
              {/* Conditional rendering of expertise cards */}
              {!sol.hideExpertiseCards && (
                <>
                  <h4 className="font-medium text-gray-700 mb-3 flex items-center">
                    <span className="w-1 h-6 mr-2 rounded" style={{ backgroundColor: sol.color }} />Areas of Expertise
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {sol.expertise.map((item,i) => <ExpertiseCard key={i} expertise={item} color={sol.color} />)}
                  </div>
                </>
              )}
              
              {/* Enhanced Why Choose Section */}
              <WhyChooseSection solution={sol} />
            </div>
          )
        ))}
      </div>

      {/* Global Styles */}
      <style jsx global>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px);} to { opacity: 1; transform: translateY(0);} }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        @keyframes pulse { 0%,100% { opacity:0.5;} 50%{ opacity:0.8;} }
        .animate-pulse { animation: pulse 2s cubic-bezier(0.4,0,0.6,1) infinite; }
        .hover\\:scale-105:hover { transform: scale(1.05); }
      `}</style>
    </div>
  );
};

export default SAPStaffingSolutions;